import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

import { useTheme } from "@/contexts/ThemeContext";
import { apiService } from "@/services/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
//
import {
  Users,
  Plus,
  Search,
  // MoreHorizontal,
  Edit,
  Trash2,
  Mail,
  User,
  Calendar,
  RefreshCw,
} from "lucide-react";

interface Admin {
  _id: string;
  fullName: string;
  email: string;
  role: string;
  createdAt: string;
  updatedAt: string;
}

interface PaginatedResponse {
  prev: boolean;
  next: boolean;
  page: number;
  item_per_page: number;
  total_page: number;
  total_count: number;
  history: Admin[];
}

export function Admins() {
  const { currentTheme } = useTheme();
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });

  // Load admins
  const loadAdmins = async (page = 1) => {
    setLoading(true);
    try {
      const response = await apiService.getAllAdmins({ page });
      if (response.type === 0) {
        const data = response.data as PaginatedResponse;
        // Normalize: backend returns `name`; UI uses `fullName`
        const normalized = data.history.map(a => ({
          ...a,
          fullName: a.fullName || (a as unknown as { name?: string }).name || "",
        }));
        setAdmins(normalized);
        setCurrentPage(data.page);
        setTotalPages(data.total_page);
        setTotalCount(data.total_count);
      } else {
        toast.error(response.message || "Failed to load admins");
      }
    } catch (error) {
      toast.error("Failed to load admins");
      console.error("Error loading admins:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAdmins();
  }, []);

  // Create admin
  const handleCreateAdmin = async () => {
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      const response = await apiService.createAdmin(formData);
      if (response.type === 0) {
        toast.success("Admin created successfully");
        setIsCreateDialogOpen(false);
        setFormData({ firstName: "", lastName: "", email: "" });
        void loadAdmins(currentPage);
      } else {
        toast.error(response.message || "Failed to create admin");
      }
    } catch (error) {
      toast.error("Failed to create admin");
      console.error("Error creating admin:", error);
    }
  };

  // Update admin
  const handleUpdateAdmin = async () => {
    if (!selectedAdmin || !formData.firstName || !formData.lastName || !formData.email) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      const response = await apiService.updateAdmin(selectedAdmin._id, {
        // Backend expects `name` field
        name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
      });
      if (response.type === 0) {
        toast.success("Admin updated successfully");
        setIsEditDialogOpen(false);
        setSelectedAdmin(null);
        setFormData({ firstName: "", lastName: "", email: "" });
        void loadAdmins(currentPage);
      } else {
        toast.error(response.message || "Failed to update admin");
      }
    } catch (error) {
      toast.error("Failed to update admin");
      console.error("Error updating admin:", error);
    }
  };

  // Delete admin
  const handleDeleteAdmin = async (adminId: string) => {
    if (!confirm("Are you sure you want to delete this admin?")) return;

    try {
      const response = await apiService.deleteAdmin(adminId);
      if (response.type === 0) {
        toast.success("Admin deleted successfully");
        void loadAdmins(currentPage);
      } else {
        toast.error(response.message || "Failed to delete admin");
      }
    } catch (error) {
      toast.error("Failed to delete admin");
      console.error("Error deleting admin:", error);
    }
  };

  // Edit admin
  const handleEditAdmin = (admin: Admin) => {
    const [firstName, ...lastNameParts] = admin.fullName.split(" ");
    const lastName = lastNameParts.join(" ");
    setFormData({
      firstName,
      lastName,
      email: admin.email,
    });
    setSelectedAdmin(admin);
    setIsEditDialogOpen(true);
  };

  // Filter admins
  const filteredAdmins = admins.filter(
    admin =>
      admin.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      admin.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Admin Management</h1>
          <p className="text-muted-foreground">
            Manage system administrators and their permissions
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            onClick={() => {
              void loadAdmins(currentPage);
            }}
            variant="outline"
            size="sm"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button style={{ backgroundColor: currentTheme.colors.primary }}>
                <Plus className="w-4 h-4 mr-2" />
                Add Admin
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Admin</DialogTitle>
                <DialogDescription>Create a new administrator account</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      value={formData.firstName}
                      onChange={e => {
                        setFormData(prev => ({ ...prev, firstName: e.target.value }));
                      }}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      value={formData.lastName}
                      onChange={e => {
                        setFormData(prev => ({ ...prev, lastName: e.target.value }));
                      }}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={e => {
                      setFormData(prev => ({ ...prev, email: e.target.value }));
                    }}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsCreateDialogOpen(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateAdmin}
                  style={{ backgroundColor: currentTheme.colors.primary }}
                >
                  Create Admin
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search admins by name or email..."
                value={searchTerm}
                onChange={e => {
                  setSearchTerm(e.target.value);
                }}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Admins Table */}
      <Card>
        <CardHeader>
          <CardTitle>Administrators</CardTitle>
          <CardDescription>{totalCount} total administrators</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin mr-2" />
              Loading admins...
            </div>
          ) : filteredAdmins.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No admins found</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAdmins.map(admin => (
                    <TableRow key={admin._id}>
                      <TableCell>
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="font-medium">{admin.fullName}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Mail className="w-4 h-4 text-muted-foreground" />
                          <span>{admin.email}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="capitalize">
                          {admin.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span>{formatDate(admin.createdAt)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            title="View Details"
                            onClick={() => {
                              handleEditAdmin(admin);
                            }}
                          >
                            <User className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Edit"
                            onClick={() => {
                              handleEditAdmin(admin);
                            }}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Delete"
                            onClick={() => {
                              void handleDeleteAdmin(admin._id);
                            }}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {(currentPage - 1) * 10 + 1} to {Math.min(currentPage * 10, totalCount)}{" "}
                    of {totalCount} results
                  </p>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        void loadAdmins(currentPage - 1);
                      }}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        void loadAdmins(currentPage + 1);
                      }}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Edit Admin Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Admin</DialogTitle>
            <DialogDescription>Update administrator information</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="editFirstName">First Name</Label>
                <Input
                  id="editFirstName"
                  value={formData.firstName}
                  onChange={e => {
                    setFormData(prev => ({ ...prev, firstName: e.target.value }));
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="editLastName">Last Name</Label>
                <Input
                  id="editLastName"
                  value={formData.lastName}
                  onChange={e => {
                    setFormData(prev => ({ ...prev, lastName: e.target.value }));
                  }}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="editEmail">Email</Label>
              <Input
                id="editEmail"
                type="email"
                value={formData.email}
                onChange={e => {
                  setFormData(prev => ({ ...prev, email: e.target.value }));
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsEditDialogOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateAdmin}
              style={{ backgroundColor: currentTheme.colors.primary }}
            >
              Update Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
